import { Component, Input, OnInit, OnDestroy, ElementRef, ChangeDetectionStrategy, AfterViewInit, ChangeDetectorRef, Inject, PLATFORM_ID, HostListener } from '@angular/core';
import Player from '@vimeo/player';
import { fromEvent, Subscription } from 'rxjs';
import { bufferTime, filter, take } from 'rxjs/operators';
import { StateService } from '../../../core/providers/state/state.service';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { NGXLogger } from 'ngx-logger';

@Component({
    selector: 'vsf-vimeo-player',
    templateUrl: './vimeo-player.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VimeoPlayerComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() source = '';
    @Input() width = 0;
    @Input() height = 0;
    @Input() fullSection = false;
    @Input() withMouseHover = false;
    @Input() previewImageUrl = '';
    @Input() previewAltText = '';

    isMuted = true;
    vimeoElementId: string;
    videoId: number;
    isMobile: boolean | null = null;
    showHint = false;
    firstInteraction = true;
    showPreview = true;

    private vimeoPlayer: Player;
    private scrollSubscription: Subscription;
    private static readonly SCROLL_SUBSCRIPTION_BUFFER_TIME = 250;
    private static readonly REGULAR_MOBILE_WIDTH_RATE = 0.9;
    private static readonly REGULAR_MOBILE_HEIGHT_RATE = 0.8;
    private static readonly FULL_WIDTH_RATE = 1.0;
    private static readonly FULL_HEIGHT_RATE = 1.0;
    mobileWidthRate = VimeoPlayerComponent.FULL_WIDTH_RATE;
    mobileHeightRate = VimeoPlayerComponent.FULL_HEIGHT_RATE;

    constructor(
        private changeDetector: ChangeDetectorRef,
        private elementRef: ElementRef,
        private logger: NGXLogger,
        @Inject(PLATFORM_ID) private platformId: object,
        private stateService: StateService) {
    }

    ngOnInit () {
        this.mobileWidthRate = this.fullSection ?
            VimeoPlayerComponent.FULL_WIDTH_RATE :
            VimeoPlayerComponent.REGULAR_MOBILE_WIDTH_RATE;
        this.mobileHeightRate = this.fullSection ?
            VimeoPlayerComponent.FULL_HEIGHT_RATE :
            VimeoPlayerComponent.REGULAR_MOBILE_HEIGHT_RATE;
        this.videoId = this.getVimeoId(this.source);
        this.vimeoElementId = `vimeo-video-${this.videoId}-${Date.now()}`;
        const isMobile$ = this.stateService.select(state => state.isMobile).pipe(take(1));
        isMobile$.subscribe(isMobile => {
            this.isMobile = isMobile;
            this.changeDetector.detectChanges();
        });
        this.updateDimensions();
    }

    ngAfterViewInit () {
        setTimeout(() => {
            this.initVimeoPlayer();
            if(this.isMobile && !this.fullSection) {
                this.initScrollListener();
            }
        });
    }

    ngOnDestroy () {
        if (this.vimeoPlayer) {
            this.vimeoPlayer.destroy();
        }
        if (this.scrollSubscription) {
            this.scrollSubscription.unsubscribe();
        }
    }

    @HostListener('window:resize', ['$event'])
    onResize() {
        this.updateDimensions();
    }

    private updateDimensions(): void {
        if(isPlatformBrowser(this.platformId) && this.isMobile && !this.fullSection) {
            this.width = window.innerWidth * this.mobileWidthRate;
            this.height = window.innerHeight * this.mobileHeightRate;
        }
        this.changeDetector.detectChanges();
    }

    private initVimeoPlayer () {
        if(isPlatformServer(this.platformId)) {
            return;
        }
        if (!this.videoId) {
            this.logger.error('Vimeo video ID is not valid');
            return;
        }
        const options: Player.Options = {
            id: this.videoId,
            controls: false,
            muted: true,
            loop: true,
            width: (this.fullSection || this.isMobile) ? this.width : undefined,
            height: (this.fullSection || this.isMobile) ? this.height : undefined,
            autoplay: (!this.withMouseHover || this.isMobile) ? true : false,
        };
        this.vimeoPlayer = new Player(this.vimeoElementId, options);

        this.vimeoPlayer?.ready().then(() => {
            const iframe = this.elementRef.nativeElement.querySelector('iframe');
            if (iframe) {
                const allow = iframe.getAttribute('allow') || '';
                const wanted = ['autoplay', 'fullscreen', 'picture-in-picture'];
                const merged = new Set(allow.split(';').map((s:any) => s.trim()).filter(Boolean).concat(wanted));
                iframe.setAttribute('allow', Array.from(merged).join('; '));
                iframe.setAttribute('playsinline', '1');
                iframe.style.pointerEvents = 'none';
                this.width = iframe.width;
                this.changeDetector.detectChanges();
            }

            this.vimeoPlayer.on('play', () => {
                if(this.showPreview) {
                    this.showPreview = false;
                    this.changeDetector.detectChanges();
                }
                if(this.firstInteraction && !this.isMuted) {
                    this.userInteractionVerified();
                }
            });
            if (!this.withMouseHover) {
                this.vimeoPlayer.play().catch(error => {
                    this.logger.warn(`Could not autoplay in fullSection mode:${error}`);
                    this.vimeoPlayer.setVolume(0).then(() => {
                        this.vimeoPlayer.play().catch(e => {
                            this.logger.error(`Failed to autoplay even with muted volume:${e}`);
                        });
                    });
                });
            }
        });
    }

    onPlayerClick (event: MouseEvent) {
        event.stopPropagation();
        event.preventDefault();
        this.stateService.setState('fullscreenPreviewOpen', this.videoId);
    }

    onPlayerKeydown (event: KeyboardEvent) {
        event.stopPropagation();
        event.preventDefault();
    }

    async toggleMute (event: MouseEvent) {
        event.stopPropagation();
        event.preventDefault();
        await this.vimeoPlayer.setVolume(this.isMuted ? 1 : 0).catch(error => {
            this.logger.error(`Error changing video volume:${error}`);
        });
        this.isMuted = !this.isMuted;
        await this.vimeoPlayer.pause().catch(error => {
            this.logger.error(`Error pausing video:${error}`);
        });
        this.changeDetector.detectChanges();

        setTimeout(async () => {
            try {
                await this.vimeoPlayer.play();
            } catch(error) {
                if(this.firstInteraction) {
                    this.showHint = true;
                    this.changeDetector.detectChanges();
                }
                this.logger.error(`Error playing video:${error}`);
            }
        }, 0);
    }

    getVimeoId (source: string): number {
        const match = source.match(/vimeo\.com\/(\d+)/);
        return match ? parseInt(match[1], 10) : 0;
    }

    onMouseEnter(event: MouseEvent) {
        if (!this.isMobile && this.vimeoPlayer && this.withMouseHover) {
            this.vimeoPlayer.play().catch(error => {
                if (error.name !== 'PlayInterrupted') {
                    this.logger.error(`Error playing video on mouse enter:${error}`);
                }
            });
        }
    }

    onMouseLeave(event: MouseEvent) {
        if (!this.isMobile && this.vimeoPlayer && this.withMouseHover) {
            this.vimeoPlayer.pause().catch(error => {
                this.logger.error(`Error pausing video on mouse leave:${error}`);
            });
        }
    }

    isElementInViewport (el: HTMLElement) {
        if(isPlatformBrowser(this.platformId)) {
            const rect = el.getBoundingClientRect();
            return (
                rect.top >= 0 &&
                rect.left >= 0 &&
                rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
                rect.right <= (window.innerWidth || document.documentElement.clientWidth)
            );
        }
    }

    private async userInteractionVerified() {
        this.showHint = false;
        this.firstInteraction = false;
        const iframe = this.elementRef.nativeElement.querySelector('iframe');
        if (iframe) {
            iframe.style.pointerEvents = 'none';
        }
        this.changeDetector.detectChanges();
        this.logger.debug('User interaction verified');
    }

    private initScrollListener() {
        if(isPlatformBrowser(this.platformId)) {
            setTimeout(() => {
                this.scrollSubscription = fromEvent(window, 'scroll', { passive: true }).pipe(
                    bufferTime(VimeoPlayerComponent.SCROLL_SUBSCRIPTION_BUFFER_TIME),
                    filter(val => 1 < val.length),
                ).subscribe(async () => {
                    if (this.vimeoPlayer) {
                        if (this.isElementInViewport(this.elementRef.nativeElement)) {
                            if (await this.vimeoPlayer.getPaused()) {
                              this.vimeoPlayer.play().catch(error => {
                                if (error.name !== 'PlayInterrupted') {
                                    this.logger.error(`Error playing video:${error}`);
                                }
                              });
                            }
                        } else {
                            if (!(await this.vimeoPlayer.getPaused())) {
                              this.vimeoPlayer.pause().catch(error => {
                                    this.logger.error(`Error pausing video:${error}`);
                              });
                            }
                        }
                    }
                });
            }, 0);
        }
    }
}
